import { useEffect, useRef, useState } from 'react';

export interface UseStructureUnsavedChangesArgs {
  /**
   * Every slice of form state the Save button commits. Order matters only in
   * that it must be the same on every render — the value is serialised whole.
   */
  formState: unknown;
  /**
   * Changes each time the server data behind the form does — pass the query's
   * `dataUpdatedAt`. A new value means the form is about to be re-seeded, so
   * the comparison starts over: the first load, and every save, since the page
   * refetches after one.
   */
  baselineKey: number;
  /** False until the structure's data has arrived. */
  isLoaded: boolean;
  /**
   * An address or contact editor is open. Its contents are not in `formState`
   * until the row is committed, so an open editor is unsaved work in its own
   * right — and Save refuses to run while one is open.
   */
  hasOpenDraft?: boolean;
}

const serialise = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    // A cycle or a non-serialisable value would otherwise take the page down
    // over a status pill. Treat it as "cannot tell" rather than "changed":
    // claiming unsaved work that does not exist teaches the user to ignore the
    // warning, which is worse than missing one.
    return '';
  }
};

/**
 * Whether the structure form holds edits **the user made** and has not saved.
 *
 * Baseline-and-compare rather than a flag raised by each handler: the page has
 * more than a dozen slices of form state and as many `onChange`s again, and one
 * handler forgetting to raise a flag is a silent failure — the pill simply never
 * appears. A serialised snapshot cannot be forgotten by a new *field*, only by a
 * new slice left out of `formState`.
 *
 * **Why the baseline waits for a gesture.** Loading a structure does not fill
 * the form once; a dozen effects copy the response in, and slower queries — site
 * schemas, attributes — land seconds later still. An earlier version took the
 * baseline once the form had been quiet for 150ms, and a query that resolved
 * after that window read as the user's own edit: the pill appeared on a page
 * nobody had touched, intermittently, which is the worst way for a warning to
 * behave. So the baseline simply tracks the form until the user first touches
 * the page. Nothing the app does to the form on its own can be mistaken for
 * something the user did, however late it arrives.
 *
 * `pointerdown` and `keydown` fire *before* the change they cause, so the
 * baseline freezes on the state as it was — the gesture's own edit is still
 * caught.
 */
export const useStructureUnsavedChanges = ({
  formState,
  baselineKey,
  isLoaded,
  hasOpenDraft = false,
}: UseStructureUnsavedChangesArgs): boolean => {
  const baseline = useRef<string | null>(null);
  const baselinedKey = useRef<number | null>(null);
  const [hasTouchedPage, setHasTouchedPage] = useState(false);

  const current = serialise(formState);

  // Listen only once the structure has loaded. A click while the loader is still
  // up would otherwise freeze the baseline on an empty form, and the seeding that
  // followed would read as the user's own edits.
  useEffect(() => {
    if (hasTouchedPage || !isLoaded) return;

    const onGesture = () => setHasTouchedPage(true);
    // Capture, so a handler that stops propagation cannot hide the gesture.
    document.addEventListener('pointerdown', onGesture, { capture: true });
    document.addEventListener('keydown', onGesture, { capture: true });

    return () => {
      document.removeEventListener('pointerdown', onGesture, { capture: true });
      document.removeEventListener('keydown', onGesture, { capture: true });
    };
  }, [hasTouchedPage, isLoaded]);

  // A save refetches and re-seeds the form, so the comparison starts over —
  // including the wait for the next gesture.
  useEffect(() => {
    if (baselinedKey.current !== null && baselinedKey.current !== baselineKey) {
      setHasTouchedPage(false);
    }
    baselinedKey.current = baselineKey;
  }, [baselineKey]);

  // Follow the form until the user touches the page, then hold. Done after
  // commit rather than during render: a render that React discards must not be
  // able to leave a baseline behind, and the value that has to survive here is
  // the last state the user actually saw.
  useEffect(() => {
    if (!hasTouchedPage) {
      baseline.current = current;
    }
  }, [current, hasTouchedPage]);

  if (hasOpenDraft) return true;
  // Nothing the app does before the first gesture counts, so there is no need to
  // consult the baseline at all — which also avoids reporting a change for the
  // one render between a commit and the effect above catching up.
  if (!isLoaded || !hasTouchedPage || baseline.current === null) return false;
  return current !== baseline.current;
};
